import React from 'react';
import styled from 'styled-components';
import { fontSize, fontWeight, spacing } from '../../style/tokens';

interface MetricGaugeProps {
  /**
   * 0–100 marker position from the left, in GAUGE space — not a raw score.
   * Compute it with scoreBands.markerPosition(id, score, bands), which maps the
   * score through that indicator's own band scale.
   */
  position: number;
  labels?: [string, string, string];
  size?: 'small' | 'large';
  labelColor?: string;
  labelFontSize?: string; // override for the under-bar band labels (default fontSize.xxs)
  bands?: 2 | 3; // 3 = orange+yellow+green (default), 2 = yellow+green only
}

const Wrapper = styled.div<{ size: 'small' | 'large' }>`
  margin-top: ${({ size }) => (size === 'large' ? '4px' : '9px')};
`;

const Track = styled.div`
  position: relative;
  height: ${({ theme }) => '7px'};
  display: flex;
  gap: 3px;
`;

const Seg = styled.span<{ color: string; pos: 1 | 2 | 3 }>`
  flex: 1;
  height: 100%;
  background: ${({ color }) => color};
  opacity: ${({ pos }) => (pos === 3 ? 1 : pos === 2 ? 0.85 : 0.85)};
  border-radius: 4px;
`;

const Marker = styled.div<{ left: number; large: boolean }>`
  position: absolute;
  top: 50%;
  left: ${({ left }) => left}%;
  transform: translate(-50%, -50%);
  width: ${({ large }) => (large ? '18px' : '13px')};
  height: ${({ large }) => (large ? '18px' : '13px')};
  border-radius: 50%;
  background: #fff;
  border: 2.5px solid #333;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  z-index: 1;
`;

const Labels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${spacing.xs};
`;

const LabelText = styled.span<{ align: 'left' | 'center' | 'right'; labelColor: string; labelFontSize: string }>`
  font-size: ${({ labelFontSize }) => labelFontSize};
  font-weight: ${fontWeight.semibold};
  color: ${({ labelColor }) => labelColor};
  flex: 1;
  text-align: ${({ align }) => align};
`;

export /**
 * Remaps a 0-100 score to a 0-100 marker position on a 2-band (yellow/green) gauge.
 * Scores that were in the red zone (0-33) fold into the lower half of yellow (0-25).
 * Scores in the yellow zone (33-66.7) map to the upper yellow half (25-50).
 * Scores in the green zone (66.7-100) map to the green half (50-100).
 *
 * Formula (threshold = 200/3 ≈ 66.667):
 *   x ≤ threshold  →  x × 50 / threshold
 *   x >  threshold →  50 + (x − threshold) × 50 / (100 − threshold)
 */
function remapForTwoBands(x: number): number {
  const threshold = 200 / 3;
  if (x <= threshold) {
    return x * 50 / threshold;
  }
  return 50 + (x - threshold) * 50 / (100 - threshold);
}

const MetricGauge: React.FC<MetricGaugeProps> = ({
  position,
  labels = ['Outside range', 'Worth noting', 'Normal'],
  size = 'small',
  labelColor = '#bbb',
  labelFontSize = fontSize.xxs,
  bands = 3,
}) => {
  const colors: [string, string, string] = ['#ff8c66', '#fde04f', '#8ecf3a'];

  // `position` arrives already in GAUGE space — mapped through the indicator's
  // own band scale by scoreBands.markerPosition, which also handles inversion
  // and the 2-band fold. Band edges are per-parameter (Protocol v2.1.2-GLP1
  // §4/§5), so this component cannot derive the position from a raw score on
  // its own; doing so here would put the marker in the wrong segment.
  const markerLeft = Math.max(2, Math.min(98, position));

  return (
    <Wrapper size={size}>
      <Track>
        {bands === 3 && <Seg color={colors[0]} pos={1} />}
        <Seg color={colors[1]} pos={2} />
        <Seg color={colors[2]} pos={3} />
        <Marker left={markerLeft} large={size === 'large'} />
      </Track>
      <Labels>
        {bands === 3
          ? <>
              <LabelText align="left"   labelColor={labelColor} labelFontSize={labelFontSize}>{labels[0]}</LabelText>
              <LabelText align="center" labelColor={labelColor} labelFontSize={labelFontSize}>{labels[1]}</LabelText>
              <LabelText align="right"  labelColor={labelColor} labelFontSize={labelFontSize}>{labels[2]}</LabelText>
            </>
          : <>
              <LabelText align="left"  labelColor={labelColor} labelFontSize={labelFontSize}>{labels[1]}</LabelText>
              <LabelText align="right" labelColor={labelColor} labelFontSize={labelFontSize}>{labels[2]}</LabelText>
            </>
        }
      </Labels>
    </Wrapper>
  );
};

export default MetricGauge;
